import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

interface PaymentToastProps {
  show: boolean;
  applicantName: string;
  amount: number;
  onClose: () => void;
}

// Animations
const slideIn = keyframes`
  from {
    transform: translateX(-120%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const confetti = keyframes`
  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
  100% { transform: translateY(-20px) rotate(360deg); opacity: 0; }
`;

const ToastContainer = styled(motion.div)`
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f4ff 100%);
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: 
    0 10px 40px rgba(106, 13, 173, 0.25),
    0 0 0 1px rgba(106, 13, 173, 0.1);
  border: 2px solid rgba(106, 13, 173, 0.2);
  max-width: 380px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #6A0DAD, #9B59B6, #6A0DAD);
    background-size: 200% auto;
    animation: ${shimmer} 2s linear infinite;
  }
`;

const IconContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6A0DAD 0%, #9B59B6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
  position: relative;
  box-shadow: 0 4px 12px rgba(106, 13, 173, 0.3);
`;

const ConfettiPiece = styled.span<{ $delay: number; $left: number }>`
  position: absolute;
  font-size: 10px;
  animation: ${confetti} 1s ease-out forwards;
  animation-delay: ${props => props.$delay}s;
  left: ${props => props.$left}%;
  top: 50%;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h4`
  margin: 0 0 4px 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6A0DAD;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Message = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #2d3748;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Amount = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  margin-top: 6px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  flex-shrink: 0;

  &:hover {
    color: #6A0DAD;
  }
`;

// Chime sound using Web Audio API
const playChimeSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Create a pleasant two-tone chime
    const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5 (C major chord)
    
    frequencies.forEach((freq, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
      
      // Envelope for nice fade in/out
      const startTime = audioContext.currentTime + (index * 0.1);
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + 0.8);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + 0.8);
    });
    
    // Add a soft "ding" on top
    setTimeout(() => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(1046.50, audioContext.currentTime); // C6
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.5);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.5);
    }, 300);
    
  } catch (error) {
    console.log('[PaymentToast] Audio not supported:', error);
  }
};

const confettiEmojis = ['🎉', '✨', '💰', '🎊', '⭐'];

const PaymentToast: React.FC<PaymentToastProps> = ({ 
  show, 
  applicantName, 
  amount, 
  onClose 
}) => {
  const hasPlayedSound = useRef(false);

  useEffect(() => {
    if (show && !hasPlayedSound.current) {
      playChimeSound();
      hasPlayedSound.current = true;
      
      // Auto-close after 6 seconds
      const timer = setTimeout(() => {
        onClose();
      }, 6000);
      
      return () => clearTimeout(timer);
    }
    
    if (!show) {
      hasPlayedSound.current = false;
    }
  }, [show, onClose]);

  return (
    <AnimatePresence>
      {show && (
        <ToastContainer
          initial={{ x: -400, opacity: 0, scale: 0.8 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          exit={{ x: -400, opacity: 0, scale: 0.8 }}
          transition={{ 
            type: 'spring', 
            stiffness: 400, 
            damping: 30,
            mass: 1
          }}
        >
          <IconContainer>
            <span role="img" aria-label="money">💵</span>
            {confettiEmojis.map((emoji, i) => (
              <ConfettiPiece key={i} $delay={i * 0.15} $left={20 + (i * 15)}>
                {emoji}
              </ConfettiPiece>
            ))}
          </IconContainer>
          
          <Content>
            <Title>
              <span>🎉</span>
              New Payment Received!
            </Title>
            <Message>{applicantName}</Message>
            <Amount>₹{amount.toLocaleString('en-IN')}</Amount>
          </Content>
          
          <CloseButton onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </CloseButton>
        </ToastContainer>
      )}
    </AnimatePresence>
  );
};

export default PaymentToast;
